import { useEffect, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router";
import { NotFoundPage } from "../NotFoundPage";
import { useAuth } from "../../shell/AuthProvider";
import { useAccess } from "../../shell/AccessProvider";
import { SectionNav } from "../../shell/SectionNav";
import { CrmSlugProvider, appUrl } from "../../lib/customerUrl";
import { CapabilityGate } from "./CapabilityGate";
import { CompaniesPage } from "./CompaniesPage";
import { CompanyDetailPage } from "./CompanyDetailPage";
import { LeadsPage } from "./LeadsPage";
import { LeadGeneratorPage } from "./LeadGeneratorPage";
import { LeadDetailPage } from "./LeadDetailPage";
import { ProductsPage } from "./ProductsPage";
import { OpportunitiesPage } from "./OpportunitiesPage";
import { OpportunityDetailPage } from "./OpportunityDetailPage";
import { QuotesPage } from "./QuotesPage";
import { OrdersPage } from "./OrdersPage";
import { QuoteDetailPage, OrderDetailPage, InvoiceDetailPage } from "./CommercialRecordPage";
import { SalesCyclePage, SalesCycleRunPage } from "./SalesCyclePage";
import { AppDesignerPage } from "./AppDesignerPage";
import { FieldOptionsPage } from "./FieldOptionsPage";
import { DocumentDesignerPage } from "./document_designer/DocumentDesignerPage";
import { DashboardDesignerPage } from "./dashboard_designer/DashboardDesignerPage";
import { DashboardListPage } from "./dashboard_designer/DashboardListPage";
import { DashboardViewPage } from "./dashboard_designer/DashboardViewPage";
import { SupportPage, InvoicesPage, MarketingPage, ForecastPage, AgreementsPage, AgreementDetailPage } from "./EnterprisePages";
import { MailboxPage } from "./MailboxPage";
import { SalesCalendarPage } from "./SalesCalendarPage";
import { ContactsPage } from "./ContactsPage";
import { FollowUpsPage } from "./FollowUpsPage";
import { ActivitiesPage } from "./ActivitiesPage";
import { NotificationsPage, SearchPage } from "./FinderPages";
import { mailboxApi } from "../../lib/mailboxApi";
import { SupportTicketPage } from "./SupportTicketPage";
import "./crm.css";

export function CrmApp() {
  const { session } = useAuth();
  const { can } = useAccess();
  const location = useLocation();
  const orgId = session?.orgId ?? "";
  const slug = session?.orgSlug ?? "";
  const href = (path: string) => appUrl("crm", slug, path);
  const openSibling = (app: "timesheet" | "expense") => {
    const marker = `/${app}`;
    const index = location.pathname.lastIndexOf(marker);
    const tail = index >= 0 ? location.pathname.slice(index + marker.length) : "";
    const path = tail.startsWith("/") ? tail : "/";
    return appUrl(app, slug, `${path}${location.search}${location.hash}`);
  };

  const heldBack = new Set(["crm.marketing"]);
  const [mailboxOn, setMailboxOn] = useState(false);
  const [mailboxQueue, setMailboxQueue] = useState(false);
  useEffect(() => {
    if (!orgId || (!can("crm.mailbox") && !can("crm.calendar"))) {
      setMailboxOn(false);
      setMailboxQueue(false);
      return;
    }
    let cancelled = false;
    mailboxApi.settings(orgId).then((row) => {
      if (!cancelled) {
        setMailboxOn(Boolean(row.mailbox.enabled));
        setMailboxQueue(Boolean(row.mailbox.queue_access));
      }
    }).catch(() => {
      if (!cancelled) {
        setMailboxOn(false);
        setMailboxQueue(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [orgId, can]);
  const navItems = [
    { to: href("/companies"), label: "Companies", capability: "crm.companies", end: false },
    { to: href("/lead-generator"), label: "LeadGen", capability: "crm.lead_generator", end: true },
    { to: href("/leads"), label: "Leads", capability: "crm.leads", end: false },
    { to: href("/agreements"), label: "Agreements", capability: "crm.agreements", end: true },
    { to: href("/products"), label: "Products", capability: "crm.products", end: true },
    { to: href("/opportunities"), label: "Opportunities", capability: "crm.opportunities", end: false },
    { to: href("/quotes"), label: "Quotes", capability: "crm.quotes", end: false },
    { to: href("/orders"), label: "Orders", capability: "crm.orders", end: false },
    { to: href("/invoices"), label: "Invoices", capability: "crm.invoices", end: false },
    { to: href("/sales-cycle"), label: "Sales cycle", capability: "crm.sales_cycle", end: false },
    { to: href("/support"), label: "Support", capability: "crm.support", end: true },
    { to: href("/forecast"), label: "Forecast & KPI", capability: "crm.forecast", end: true },
    { to: href("/app-designer"), label: "App Designer", capability: "crm.app.design", end: true },
    { to: href("/field-options"), label: "Field Options", capability: "crm.field_options", end: true },
    { to: href("/dashboard-designer"), label: "Dashboards", capability: "crm.dashboard.design", end: false },
    { to: href("/document-designer"), label: "Template Designer", capability: "crm.documents", end: true },
    { to: href("/marketing"), label: "Marketing", capability: "crm.marketing", end: true },
    { to: href("/mailbox"), label: "Mailbox", capability: "crm.mailbox", end: true },
    { to: href("/calendar"), label: "Calendar", capability: "crm.calendar", end: true },
  ].filter((item) => can(item.capability) && !heldBack.has(item.capability))
    .filter((item) => item.capability !== "crm.calendar" || mailboxOn)
    .filter((item) => item.capability !== "crm.mailbox" || (mailboxOn && mailboxQueue));
  const home = navItems[0]?.to ?? "/dashboard";
  const designerPath = href("/dashboard-designer");
  const sectionItems = navItems.map((item) => ({
    to: item.to,
    label: item.label,
    end: item.end,
    dataTour: `crm-tab-${item.capability}`,
  }));

  return (
    <CrmSlugProvider slug={slug}>
    <div className="crm-app">
      <SectionNav label="CRM sections" items={sectionItems} />
      <div className="crm-canvas">
        <Routes>
          <Route path="/" element={<Navigate to={home} replace />} />
          <Route path="/companies" element={<CompaniesPage orgId={orgId} />} />
          <Route path="/companies/:companyId" element={<CompanyDetailPage orgId={orgId} />} />
          <Route path="/contacts" element={<ContactsPage orgId={orgId} />} />
          <Route path="/lead-generator" element={<CapabilityGate capability="crm.lead_generator"><LeadGeneratorPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/follow-ups" element={<FollowUpsPage orgId={orgId} />} />
          <Route path="/leads" element={<LeadsPage orgId={orgId} />} />
          <Route path="/leads/:leadId" element={<LeadDetailPage orgId={orgId} />} />
          <Route path="/agreements" element={<CapabilityGate capability="crm.agreements"><AgreementsPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/agreements/:agreementId" element={<CapabilityGate capability="crm.agreements"><AgreementDetailPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/products" element={<ProductsPage orgId={orgId} />} />
          <Route path="/opportunities" element={<CapabilityGate capability="crm.opportunities"><OpportunitiesPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/opportunities/:opportunityId" element={<CapabilityGate capability="crm.opportunities"><OpportunityDetailPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/quotes" element={<CapabilityGate capability="crm.quotes"><QuotesPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/quotes/:quoteId" element={<CapabilityGate capability="crm.quotes"><QuoteDetailPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/orders" element={<CapabilityGate capability="crm.orders"><OrdersPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/orders/:orderId" element={<CapabilityGate capability="crm.orders"><OrderDetailPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/invoices" element={<CapabilityGate capability="crm.invoices"><InvoicesPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/invoices/:invoiceId" element={<CapabilityGate capability="crm.invoices"><InvoiceDetailPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/sales-cycle" element={<CapabilityGate capability="crm.sales_cycle"><SalesCyclePage orgId={orgId} /></CapabilityGate>} />
          <Route path="/sales-cycle/:runId" element={<CapabilityGate capability="crm.sales_cycle"><SalesCycleRunPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/support" element={<CapabilityGate capability="crm.support"><SupportPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/support/:ticketId" element={<CapabilityGate capability="crm.support"><SupportTicketPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/calendar" element={<CapabilityGate capability="crm.calendar"><SalesCalendarPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/calendar/:eventId" element={<CapabilityGate capability="crm.calendar"><SalesCalendarPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/activities" element={<ActivitiesPage orgId={orgId} />} />
          <Route path="/tasks" element={<ActivitiesPage orgId={orgId} />} />
          <Route path="/targets" element={<CapabilityGate capability="crm.forecast"><ForecastPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/reports" element={<CapabilityGate capability="crm.forecast"><ForecastPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/search" element={<SearchPage orgId={orgId} />} />
          <Route path="/notifications" element={<NotificationsPage orgId={orgId} />} />
          <Route path="/profile" element={<Navigate to={appUrl("crm", slug, "/account")} replace />} />
          <Route path="/marketing" element={<CapabilityGate capability="crm.marketing"><MarketingPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/forecast" element={<CapabilityGate capability="crm.forecast"><ForecastPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/app-designer" element={<CapabilityGate capability="crm.app.design"><AppDesignerPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/field-options" element={<CapabilityGate capability="crm.field_options"><FieldOptionsPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/dashboard-designer" element={<CapabilityGate capability="crm.dashboard.design"><DashboardListPage orgId={orgId} designerBasePath={designerPath} /></CapabilityGate>} />
          <Route path="/dashboard-designer/:dashboardId" element={<CapabilityGate capability="crm.dashboard.view"><DashboardViewPage orgId={orgId} listPath={designerPath} /></CapabilityGate>} />
          <Route path="/dashboard-designer/:dashboardId/designer" element={<CapabilityGate capability="crm.dashboard.design"><DashboardDesignerPage orgId={orgId} listPath={designerPath} /></CapabilityGate>} />
          <Route path="/document-designer" element={<CapabilityGate capability="crm.documents.quote.design"><DocumentDesignerPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/fields" element={<Navigate to={href("/app-designer")} replace />} />
          <Route path="/mailbox" element={<CapabilityGate capability="crm.mailbox"><MailboxPage orgId={orgId} /></CapabilityGate>} />
          <Route path="/timesheet" element={<Navigate to={openSibling("timesheet")} replace />} />
          <Route path="/timesheet/*" element={<Navigate to={openSibling("timesheet")} replace />} />
          <Route path="/expense" element={<Navigate to={openSibling("expense")} replace />} />
          <Route path="/expense/*" element={<Navigate to={openSibling("expense")} replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </div>
    </CrmSlugProvider>
  );
}
